import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/shared/Badges";
import {
  Search, Bell, Sparkles, ChevronDown, Check, Command as CmdIcon, LogOut,
  Settings as SettingsIcon, HelpCircle, MessageSquare, ListTodo, Inbox, Mail, CheckCheck, Plus,
  UserCircle, Users,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { timeAgo } from "@/lib/format";
import api from "@/lib/api";

// Small icon button with an unread badge.
const ToolButton = ({ icon: Icon, count, label, testid, active }) => (
  <span
    data-testid={testid}
    className={`relative h-9 w-9 rounded-xl flex items-center justify-center transition-colors cursor-pointer ${active ? "bg-slate-100 text-slate-900" : "hover:bg-slate-100 text-slate-600"}`}
    aria-label={label} title={label}
  >
    <Icon className="h-[18px] w-[18px]" />
    {count > 0 && (
      <span data-testid={`${testid}-badge`} className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-[#7C3AED] text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
        {count > 9 ? "9+" : count}
      </span>
    )}
  </span>
);

const MessagesPanel = ({ onCountChange }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("internal");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (channel) => {
    setLoading(true);
    try { const { data } = await api.get(`/messages?channel=${channel}`); setItems(data.items || []); }
    catch { setItems([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(tab); }, [tab, load]);

  const open = async (m) => {
    if (!m.read) {
      try { await api.post(`/messages/${m.id}/read`); } catch {}
      setItems((prev) => prev.map((x) => x.id === m.id ? { ...x, read: true } : x));
      onCountChange?.();
    }
  };
  const markAll = async () => {
    try { await api.post(`/messages/read-all?channel=${tab}`); } catch {}
    setItems((prev) => prev.map((x) => ({ ...x, read: true }))); onCountChange?.();
  };

  return (
    <div data-testid="messages-panel">
      <div className="flex items-center justify-between px-4 h-12 border-b border-slate-100">
        <div className="text-sm font-semibold text-slate-900 flex items-center gap-2"><MessageSquare className="h-4 w-4 text-[#7C3AED]" />Messages</div>
        <button onClick={markAll} data-testid="messages-mark-all" className="text-[11px] font-medium text-slate-500 hover:text-slate-800 flex items-center gap-1"><CheckCheck className="h-3.5 w-3.5" />Mark all read</button>
      </div>
      <div className="flex gap-1 p-2 border-b border-slate-100">
        {[["internal", "Team Inbox", Inbox], ["email", "Email", Mail]].map(([k, lbl, Icon]) => (
          <button key={k} data-testid={`messages-tab-${k}`} onClick={() => setTab(k)}
            className={`flex-1 flex items-center justify-center gap-1.5 h-8 rounded-lg text-xs font-semibold transition-colors ${tab === k ? "aurelo-gradient text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            <Icon className="h-3.5 w-3.5" />{lbl}
          </button>
        ))}
      </div>
      <div className="max-h-[320px] overflow-y-auto p-2">
        {loading && <div className="py-8 text-center text-sm text-slate-400">Loading…</div>}
        {!loading && items.length === 0 && <div className="py-8 text-center text-sm text-slate-400">No messages.</div>}
        {!loading && items.map((m) => (
          <button key={m.id} onClick={() => open(m)} className={`w-full text-left flex gap-3 rounded-xl p-2.5 transition-colors ${m.read ? "hover:bg-slate-50" : "bg-violet-50/60 hover:bg-violet-50"}`}>
            <img src={m.from_avatar} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className={`text-sm truncate ${m.read ? "font-medium text-slate-700" : "font-bold text-slate-900"}`}>{m.from_name}{m.from_company ? ` · ${m.from_company}` : ""}</span>
                <span className="text-[10px] text-slate-400 shrink-0">{timeAgo(m.created_at)}</span>
              </div>
              <div className={`text-xs truncate ${m.read ? "text-slate-500" : "text-slate-800 font-semibold"}`}>{m.subject}</div>
              <div className="text-[11px] text-slate-400 truncate mt-0.5">{m.preview}</div>
            </div>
            {!m.read && <span className="h-2 w-2 rounded-full bg-[#7C3AED] shrink-0 mt-1.5" />}
          </button>
        ))}
      </div>
      <button onClick={() => navigate("/app/contacts")} className="w-full h-10 text-xs font-semibold text-[#7C3AED] hover:bg-slate-50 border-t border-slate-100">Open inbox</button>
    </div>
  );
};

const TasksPanel = ({ onCountChange }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [adding, setAdding] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/tasks?status=open&limit=50"); setItems(data.items || []); }
    catch { setItems([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const add = async () => {
    const t = title.trim();
    if (!t || adding) return;
    setAdding(true);
    try {
      const { data } = await api.post("/tasks", { title: t, type: "follow-up", priority: "Normal", status: "open" });
      setItems((prev) => [data, ...prev]); setTitle(""); onCountChange?.();
    } catch {} finally { setAdding(false); }
  };
  const complete = async (t) => {
    setItems((prev) => prev.filter((x) => x.id !== t.id)); onCountChange?.();
    try { await api.put(`/tasks/${t.id}`, { ...t, status: "completed" }); } catch { load(); }
  };
  const DOT = { Critical: "bg-red-500", High: "bg-amber-500", Normal: "bg-slate-300", Low: "bg-slate-200" };
  return (
    <div data-testid="tasks-panel">
      <div className="flex items-center justify-between px-4 h-12 border-b border-slate-100">
        <div className="text-sm font-semibold text-slate-900 flex items-center gap-2"><ListTodo className="h-4 w-4 text-[#7C3AED]" />Tasks & To-dos</div>
        <span className="text-[11px] font-medium text-slate-400">{items.length} open</span>
      </div>
      {/* Quick add */}
      <div className="flex items-center gap-2 p-2.5 border-b border-slate-100">
        <input
          data-testid="task-quick-add-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          placeholder="+ New task…  (press Enter)"
          className="flex-1 h-9 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/30 focus:border-[#7C3AED] focus:bg-white transition-colors"
        />
        <button data-testid="task-quick-add-btn" onClick={add} disabled={!title.trim() || adding}
          className="h-9 w-9 rounded-lg aurelo-gradient text-white flex items-center justify-center shrink-0 disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity" title="Add task">
          <Plus className="h-4 w-4" />
        </button>
      </div>
      <div className="max-h-[300px] overflow-y-auto p-2">
        {loading && <div className="py-8 text-center text-sm text-slate-400">Loading…</div>}
        {!loading && items.length === 0 && <div className="py-8 text-center text-sm text-slate-400">You're all caught up 🎉</div>}
        {!loading && items.map((t) => (
          <div key={t.id} className="group flex items-center gap-2.5 rounded-xl p-2.5 hover:bg-slate-50 transition-colors">
            <button data-testid={`task-complete-${t.id}`} onClick={() => complete(t)} title="Mark complete"
              className="h-5 w-5 rounded-md border-2 border-slate-300 hover:border-[#10B981] hover:bg-emerald-50 flex items-center justify-center shrink-0 transition-colors">
              <Check className="h-3 w-3 text-transparent group-hover:text-[#10B981]" />
            </button>
            <span className={`h-2 w-2 rounded-full shrink-0 ${DOT[t.priority] || "bg-slate-300"}`} />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-slate-800 truncate font-medium">{t.title}</div>
              <div className="text-[11px] text-slate-400 capitalize">{t.type}{t.due_date ? ` · due ${t.due_date}` : ""}</div>
            </div>
          </div>
        ))}
      </div>
      <button onClick={() => navigate("/app/tasks")} className="w-full h-10 text-xs font-semibold text-[#7C3AED] hover:bg-slate-50 border-t border-slate-100">View all tasks</button>
    </div>
  );
};

export const TopBar = ({ onOpenCommand, onOpenAi, onOpenNotifications, unread }) => {
  const { user, tenant, tenants, switchTenant, logout } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ messages: 0, tasks: 0, alerts: unread || 0 });

  const refreshCounts = useCallback(async () => {
    try {
      const [m, t, n] = await Promise.all([
        api.get("/messages/summary").catch(() => ({ data: {} })),
        api.get("/tasks?status=open&limit=1").catch(() => ({ data: {} })),
        api.get("/notifications").catch(() => ({ data: {} })),
      ]);
      setCounts({
        messages: m.data?.unread || 0,
        tasks: t.data?.total || 0,
        alerts: n.data?.unread ?? 0,
      });
    } catch {}
  }, []);

  // Live badge refresh: initial fetch + poll every 30s + refresh on window focus.
  useEffect(() => {
    refreshCounts();
    const id = setInterval(refreshCounts, 30000);
    const onFocus = () => refreshCounts();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [refreshCounts]);

  // Keep alerts badge in sync when the parent updates the unread prop (e.g. after opening the panel).
  useEffect(() => { setCounts((c) => ({ ...c, alerts: unread ?? c.alerts })); }, [unread]);

  return (
    <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-slate-200/70 h-16 flex items-center gap-3 px-4 md:px-6">
      {/* Tenant switcher */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="tenant-switcher" className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium hover:bg-slate-50 transition-colors max-w-[220px]">
            <div className="h-6 w-6 rounded-lg aurelo-gradient flex items-center justify-center text-white text-xs font-bold shrink-0">{(tenant?.name || "A")[0]}</div>
            <span className="truncate">{tenant?.name || "Workspace"}</span>
            <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel className="text-xs text-slate-500">Your workspaces</DropdownMenuLabel>
          {tenants.map((t) => (
            <DropdownMenuItem key={t.id} onClick={() => switchTenant(t.id)} className="gap-2">
              <div className="h-6 w-6 rounded-lg aurelo-gradient flex items-center justify-center text-white text-xs font-bold">{t.name[0]}</div>
              <div className="flex-1"><div className="text-sm font-medium">{t.name}</div><div className="text-[11px] text-slate-400">{t.plan} · {t.role}</div></div>
              {t.id === tenant?.id && <Check className="h-4 w-4 text-emerald-600" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Search / command trigger */}
      <button data-testid="command-bar-trigger" onClick={onOpenCommand}
        className="flex-1 max-w-[420px] hidden sm:flex items-center gap-2 h-9 rounded-xl bg-slate-50 border border-slate-200 px-3 text-sm text-slate-400 hover:bg-slate-100 transition-colors">
        <Search className="h-4 w-4" />
        <span>Search or ask Aurelo AI…</span>
        <kbd className="ml-auto inline-flex items-center gap-0.5 rounded border border-slate-200 bg-white px-1.5 text-[10px] font-medium text-slate-500"><CmdIcon className="h-3 w-3" />K</kbd>
      </button>

      <div className="flex items-center gap-1 ml-auto">
        <button data-testid="open-ai-copilot" onClick={onOpenAi} className="inline-flex items-center gap-1.5 h-9 rounded-xl px-3 text-sm font-medium text-white aurelo-gradient shadow-[0_8px_20px_rgba(59,130,246,0.18)] hover:shadow-[0_12px_26px_rgba(59,130,246,0.22)] transition-shadow mr-1">
          <Sparkles className="h-4 w-4" /> <span className="hidden lg:inline">Aurelo AI</span>
        </button>

        {/* Messages */}
        <Popover onOpenChange={(o) => { if (!o) refreshCounts(); }}>
          <PopoverTrigger asChild>
            <button data-testid="messages-trigger" className="focus:outline-none"><ToolButton icon={MessageSquare} count={counts.messages} label="Messages" testid="messages-tool" /></button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[380px] p-0 overflow-hidden"><MessagesPanel onCountChange={refreshCounts} /></PopoverContent>
        </Popover>

        {/* Tasks / To-dos */}
        <Popover onOpenChange={(o) => { if (!o) refreshCounts(); }}>
          <PopoverTrigger asChild>
            <button data-testid="tasks-trigger" className="focus:outline-none"><ToolButton icon={ListTodo} count={counts.tasks} label="Tasks & To-dos" testid="tasks-tool" /></button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[360px] p-0 overflow-hidden"><TasksPanel onCountChange={refreshCounts} /></PopoverContent>
        </Popover>

        {/* Alerts / Notifications */}
        <button data-testid="notifications-trigger" onClick={() => { onOpenNotifications?.(); setTimeout(refreshCounts, 900); }} className="relative h-9 w-9 rounded-xl hover:bg-slate-100 flex items-center justify-center transition-colors text-slate-600" aria-label="Alerts" title="Alerts">
          <Bell className="h-[18px] w-[18px]" />
          {counts.alerts > 0 && <span data-testid="alerts-tool-badge" className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">{counts.alerts > 9 ? "9+" : counts.alerts}</span>}
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu" className="ml-1 rounded-full"><Avatar name={user?.name} src={user?.avatar} size={34} /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5"><div className="text-sm font-semibold">{user?.name}</div><div className="text-xs text-slate-400">{user?.email}</div></div>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="menu-my-account" onClick={() => navigate("/app/account")}><UserCircle className="h-4 w-4 mr-2" />My Account</DropdownMenuItem>
            <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/app/settings")}><SettingsIcon className="h-4 w-4 mr-2" />Settings</DropdownMenuItem>
            <DropdownMenuItem data-testid="menu-help"><HelpCircle className="h-4 w-4 mr-2" />Help & Support</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="menu-signout" onClick={logout} className="text-red-600"><LogOut className="h-4 w-4 mr-2" />Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};
